import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { User } from "firebase/auth";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  BadgePercent,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Copy,
  Download,
  ExternalLink,
  FileDown,
  FileText,
  Heart,
  LifeBuoy,
  LoaderCircle,
  Mail,
  MessageCircle,
  Package,
  ReceiptText,
  RefreshCw,
  ShieldCheck,
  ShoppingBag,
  Star,
  UserRound,
  UserX,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/features/auth/AuthContext";
import { AccountEpisodeContributions } from "@/features/podcast/community/AccountEpisodeContributions";
import { useCatalog } from "@/features/sales/storefront/catalog/CatalogContext";
import { PageIntro } from "@/features/sales/storefront/components/PageIntro";
import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import { useCurrency } from "@/features/sales/storefront/currency/CurrencyContext";
import { trackEvent, trackEventOnce } from "@/lib/analytics";
import { appCheckFetch } from "@/lib/firebase";

export const Route = createFileRoute("/cuenta")({
  head: () => ({
    meta: [
      { title: "Mi cuenta | Socrattica" },
      {
        name: "description",
        content: "Consulta tus compras, recibos, descargas y datos de cuenta en Socrattica.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CustomerAccountPage,
});

type CurrencyCode = "COP" | "USD" | "EUR" | "MXN";
type OrderStatus = "paid" | "pending" | "failed" | "refunded";

interface CustomerOrderItem {
  productId: string;
  title: string;
  coverImage?: string;
  quantity: number;
  unitPrice: number;
  currency: CurrencyCode;
  canDownload: boolean;
  fileName?: string;
}

interface CustomerOrder {
  id: string;
  status: OrderStatus;
  createdAt?: string;
  paidAt?: string;
  total: number;
  currency: CurrencyCode;
  provider?: string;
  transactionId?: string;
  receiptUrl?: string;
  items: CustomerOrderItem[];
}

interface CustomerAccountResponse {
  orders?: CustomerOrder[];
  coupons?: Array<{
    code: string;
    percent: number;
    status: "available" | "used" | "expired";
    createdAt?: string;
    expiresAt?: string;
    usedAt?: string;
  }>;
  favorites?: Array<{
    id: string;
    slug: string;
    title: string;
    coverImage?: string;
  }>;
  privacyRequests?: Array<{
    id: string;
    type: string;
    status: string;
    createdAt?: string;
    updatedAt?: string;
  }>;
  message?: string;
}

const STATUS_DETAILS = {
  paid: {
    label: "Aprobado",
    Icon: CheckCircle2,
    className: "bg-black text-white",
  },
  pending: {
    label: "En proceso",
    Icon: Clock3,
    className: "border border-black/20 bg-white text-black",
  },
  failed: {
    label: "No aprobado",
    Icon: XCircle,
    className: "bg-black/[0.06] text-black/55",
  },
  refunded: {
    label: "Reembolsado",
    Icon: RefreshCw,
    className: "bg-black/[0.06] text-black/55",
  },
} satisfies Record<OrderStatus, { label: string; Icon: typeof CheckCircle2; className: string }>;

function formatDate(value?: string, includeTime = false) {
  if (!value) return "Sin fecha disponible";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha disponible";

  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "medium",
    ...(includeTime ? { timeStyle: "short" as const } : {}),
  }).format(date);
}

function CustomerAccountPage() {
  const navigate = useNavigate();
  const { user, role, isAdmin, isLoading, resetPassword, resendVerification } = useAuth();
  const { formatPrice, toCop } = useCurrency();
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [favorites, setFavorites] = useState<NonNullable<CustomerAccountResponse["favorites"]>>([]);
  const [coupons, setCoupons] = useState<NonNullable<CustomerAccountResponse["coupons"]>>([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingDownload, setPendingDownload] = useState("");
  const [pendingReceipt, setPendingReceipt] = useState("");
  const [privacyRequests, setPrivacyRequests] = useState<
    NonNullable<CustomerAccountResponse["privacyRequests"]>
  >([]);
  const [privacyAction, setPrivacyAction] = useState<"export" | "delete" | null>(null);
  const [accountAction, setAccountAction] = useState<"verify" | "password" | null>(null);

  useEffect(() => {
    if (!isLoading && !user) {
      void navigate({ to: "/acceso", replace: true });
    } else if (!isLoading && user && isAdmin) {
      void navigate({ to: "/admin/dashboard", replace: true });
    }
  }, [isAdmin, isLoading, navigate, user]);

  useEffect(() => {
    if (isLoading || !user || isAdmin || role !== "user") return;
    const controller = new AbortController();
    let active = true;

    async function loadOrders() {
      setOrdersLoading(true);
      setOrdersError("");

      try {
        const response = await appCheckFetch("/api/customer-account", {
          headers: { Authorization: `Bearer ${await user!.getIdToken()}` },
          signal: controller.signal,
        });
        const data = (await response.json().catch(() => ({}))) as CustomerAccountResponse;
        if (!response.ok) {
          throw new Error(data.message || "No pudimos consultar tu historial de compras.");
        }
        if (active) {
          const nextOrders = data.orders ?? [];
          setOrders(nextOrders);
          setFavorites(data.favorites ?? []);
          setCoupons(data.coupons ?? []);
          setPrivacyRequests(data.privacyRequests ?? []);
          for (const order of nextOrders.filter((order) => order.status === "refunded")) {
            void trackEventOnce(`refund:${order.id}`, "refund", {
              transaction_id: order.transactionId ?? order.id,
              value: order.total,
              currency: order.currency,
              items: order.items.map((item) => ({
                item_id: item.productId,
                item_name: item.title,
                price: item.unitPrice,
                quantity: item.quantity,
              })),
            });
          }
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        if (active) {
          setOrdersError(
            error instanceof Error ? error.message : "No pudimos consultar tus compras.",
          );
        }
      } finally {
        if (active) setOrdersLoading(false);
      }
    }

    void loadOrders();
    return () => {
      active = false;
      controller.abort();
    };
  }, [isAdmin, isLoading, refreshKey, role, user]);

  const summary = useMemo(() => {
    const approved = orders.filter((order) => order.status === "paid");
    return {
      orders: approved.length,
      resources: approved.reduce(
        (total, order) =>
          total + order.items.reduce((itemTotal, item) => itemTotal + item.quantity, 0),
        0,
      ),
      downloads: approved.reduce(
        (total, order) => total + order.items.filter((item) => item.canDownload).length,
        0,
      ),
      invested: approved.reduce((total, order) => total + toCop(order.total, order.currency), 0),
    };
  }, [orders, toCop]);

  async function claimNewsletterCoupon() {
    if (!customerEmail) return;
    setCouponLoading(true);
    try {
      const response = await appCheckFetch("/api/newsletter-discount", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: customerEmail }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        code?: string;
        percent?: number;
        expiresAt?: string;
        message?: string;
      };
      if (!response.ok || !data.code) {
        throw new Error(data.message || "No pudimos obtener tu cupón.");
      }
      setCoupons([{
        code: data.code,
        percent: data.percent ?? 15,
        status: "available",
        expiresAt: data.expiresAt,
      }]);
      toast.success("Tu cupón ya está disponible en la cuponera.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos obtener tu cupón.");
    } finally {
      setCouponLoading(false);
    }
  }

  async function copyCoupon(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Cupón copiado.");
    } catch {
      toast.error("No pudimos copiar el cupón.");
    }
  }

  if (isLoading || !user || isAdmin || role !== "user") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white" role="status">
        <LoaderCircle className="h-6 w-6 animate-spin" />
        <span className="sr-only">Cargando tu cuenta</span>
      </div>
    );
  }

  const firstName = user.displayName?.trim().split(/\s+/)[0] || "Hola";
  const customerName = user.displayName || "Cliente de Socrattica";
  const customerEmail = user.email || "";

  async function handleDownload(orderId: string, item: CustomerOrderItem) {
    const key = `${orderId}:${item.productId}`;
    setPendingDownload(key);
    try {
      const response = await appCheckFetch("/api/customer-account", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await user!.getIdToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "download", orderId, productId: item.productId }),
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(data.message || "No pudimos preparar la descarga.");
      }
      const file = await response.blob();
      const url = URL.createObjectURL(file);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = item.fileName || `${item.title.replace(/[^\p{L}\p{N}._-]+/gu, "-")}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      void trackEvent("file_download", {
        item_id: item.productId,
        item_name: item.title,
        order_id: orderId,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos descargar el archivo.");
    } finally {
      setPendingDownload("");
    }
  }

  async function handleReceipt(order: CustomerOrder) {
    if (order.receiptUrl) {
      window.open(order.receiptUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setPendingReceipt(order.id);
    try {
      const response = await appCheckFetch("/api/customer-account", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await user!.getIdToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "receipt", orderId: order.id }),
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(data.message || "No pudimos preparar el comprobante.");
      }
      const url = URL.createObjectURL(await response.blob());
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos abrir el comprobante.");
    } finally {
      setPendingReceipt("");
    }
  }

  async function runAccountAction(
    action: "verify" | "password",
    callback: () => Promise<void>,
    successMessage?: string,
  ) {
    setAccountAction(action);
    try {
      await callback();
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos completar la acción.");
    } finally {
      setAccountAction(null);
    }
  }

  async function exportPersonalData() {
    setPrivacyAction("export");
    try {
      const response = await appCheckFetch("/api/customer-account", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await user!.getIdToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "export-data" }),
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(data.message || "No pudimos exportar tus datos.");
      }
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "socrattica-mis-datos.json";
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Preparamos una copia de tus datos.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos exportar tus datos.");
    } finally {
      setPrivacyAction(null);
    }
  }

  async function requestAccountDeletion() {
    if (
      !window.confirm(
        "Registraremos una solicitud para eliminar o anonimizar tu cuenta. Las transacciones que deban conservarse por obligación legal no se borrarán hasta que corresponda. ¿Deseas continuar?",
      )
    )
      return;
    setPrivacyAction("delete");
    try {
      const response = await appCheckFetch("/api/customer-account", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await user!.getIdToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "request-account-deletion" }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        message?: string;
        requestId?: string;
        status?: string;
      };
      if (!response.ok) throw new Error(data.message || "No pudimos registrar la solicitud.");
      toast.success(data.message || "Solicitud registrada.");
      setRefreshKey((current) => current + 1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos registrar la solicitud.");
    } finally {
      setPrivacyAction(null);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-white text-black">
      <SiteHeader />
      <main id="main-content" className="flex-1">
        <PageIntro
          compact
          eyebrow="Mi espacio"
          title={`Hola, ${firstName}.`}
          description="Tus compras, recibos y recursos digitales, reunidos en un solo lugar."
          aside={
            <div>
              <p className="text-5xl font-semibold tracking-[-0.06em]">{summary.resources}</p>
              <p className="mt-2 text-xs text-white/50">
                recurso{summary.resources === 1 ? "" : "s"} en tu biblioteca
              </p>
            </div>
          }
          details={["Compras", "Recibos", "Descargas"]}
        />

        <section className="py-5 sm:py-5">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            {!user.emailVerified && (
              <div className="mb-4 flex flex-col justify-between gap-4 rounded-xl border border-black bg-black p-5 text-white sm:flex-row sm:items-center">
                <div className="flex gap-3">
                  <Mail className="mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">
                      Verifica tu correo para vincular compras
                    </p>
                    <p className="mt-1 text-xs leading-5 text-white/55">
                      Así podremos encontrar también los pedidos realizados antes de crear tu
                      cuenta.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="border-white bg-white text-black hover:bg-black hover:text-white"
                  disabled={accountAction !== null}
                  onClick={() =>
                    void runAccountAction(
                      "verify",
                      resendVerification,
                      "Te enviamos un nuevo correo de verificación.",
                    )
                  }
                >
                  {accountAction === "verify" ? (
                    <LoaderCircle className="animate-spin" />
                  ) : (
                    <Mail />
                  )}
                  Reenviar verificación
                </Button>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 ">
              <SummaryCard
                icon={<ShoppingBag />}
                label="Compras aprobadas"
                value={String(summary.orders)}
              />
              <SummaryCard
                icon={<BookOpen />}
                label="Recursos adquiridos"
                value={String(summary.resources)}
              />
              <SummaryCard
                icon={<Download />}
                label="Descargas disponibles"
                value={String(summary.downloads)}
              />
              <SummaryCard
                icon={<ReceiptText />}
                label="Total de compras"
                value={formatPrice(summary.invested, "COP")}
                compact
              />
            </div>

            <Tabs
              defaultValue="library"
              className="mt-6 grid items-start gap-6 lg:grid-cols-[230px_minmax(0,1fr)]"
            >
              <TabsList
                className="grid h-auto w-full grid-cols-2 rounded-xl bg-black p-2 text-white sm:grid-cols-5 lg:sticky lg:top-20 lg:grid-cols-1 lg:gap-2 lg:p-3"
                aria-label="Secciones de mi cuenta"
              >
                <TabsTrigger
                  value="library"
                  className="min-h-12 gap-2 rounded-lg bg-transparent px-3 text-xs text-white/65 data-[state=active]:bg-white data-[state=active]:text-black lg:justify-start"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>Biblioteca</span>
                  <span className="rounded-full bg-current/10 px-2 py-0.5 text-xs">
                    {summary.resources}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="favorites"
                  className="min-h-12 gap-2 rounded-lg bg-transparent px-3 text-xs text-white/65 data-[state=active]:bg-white data-[state=active]:text-black lg:justify-start"
                >
                  <Heart className="h-4 w-4" />
                  <span>Favoritos</span>
                  <span className="rounded-full bg-current/10 px-2 py-0.5 text-xs">
                    {favorites.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="contributions"
                  className="min-h-12 gap-2 rounded-lg bg-transparent px-3 text-xs text-white/65 data-[state=active]:bg-white data-[state=active]:text-black lg:justify-start"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>Mis aportes</span>
                </TabsTrigger>
                <TabsTrigger
                  value="coupons"
                  className="min-h-12 gap-2 rounded-lg bg-transparent px-3 text-xs text-white/65 data-[state=active]:bg-white data-[state=active]:text-black lg:justify-start"
                >
                  <BadgePercent className="h-4 w-4" />
                  <span>Mis cupones</span>
                  <span className="rounded-full bg-current/10 px-2 py-0.5 text-xs">
                    {coupons.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="profile"
                  className="min-h-12 gap-2 rounded-lg bg-transparent px-3 text-xs text-white/65 data-[state=active]:bg-white data-[state=active]:text-black lg:justify-start"
                >
                  <UserRound className="h-4 w-4" />
                  <span>Perfil</span>
                </TabsTrigger>
              </TabsList>

              <div className="min-w-0">
                <TabsContent value="library" className="mt-0">
                  <section className="min-w-0" aria-labelledby="customer-orders-title">
                    <div className="flex items-end justify-between gap-4 border-b border-black pb-5">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black/55">
                          Historial
                        </p>
                        <h2
                          id="customer-orders-title"
                          className="mt-1 text-2xl font-semibold tracking-[-0.04em]"
                        >
                          Mis compras y recibos
                        </h2>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={ordersLoading}
                        onClick={() => setRefreshKey((current) => current + 1)}
                        className="border border-black"
                      >
                        <RefreshCw className={ordersLoading ? "animate-spin" : ""} />
                        <span className="hidden sm:inline">Actualizar</span>
                      </Button>
                    </div>

                    {ordersLoading ? (
                      <div
                        className="flex items-center gap-3 py-16 text-sm text-black/55"
                        role="status"
                      >
                        <LoaderCircle className="h-5 w-5 animate-spin" />
                        Consultando tus compras…
                      </div>
                    ) : ordersError ? (
                      <div className="mt-6 rounded-xl border border-black/15 p-6">
                        <div className="flex gap-3">
                          <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold">No pudimos cargar tu historial</p>
                            <p className="mt-1 text-sm leading-6 text-black/60">{ordersError}</p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-4"
                              onClick={() => setRefreshKey((current) => current + 1)}
                            >
                              Reintentar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : orders.length === 0 ? (
                      <div className="mt-6 rounded-xl border border-black/15 px-6 py-12 text-center">
                        <Package className="mx-auto h-7 w-7" />
                        <h3 className="mt-4 text-lg font-semibold">
                          Aún no hay compras vinculadas
                        </h3>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-black/60">
                          Los pedidos realizados con {customerEmail || "tu correo"} aparecerán aquí
                          cuando el pago sea registrado.
                        </p>
                        <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
                          <Button asChild>
                            <Link to="/productos/catalogo">
                              Explorar la tienda
                              <ArrowRight />
                            </Link>
                          </Button>
                          <Button asChild variant="outline">
                            <Link to="/productos/tickets">Reportar una compra</Link>
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-6 space-y-4">
                        {orders.map((order) => (
                          <OrderCard
                            key={order.id}
                            order={order}
                            user={user}
                            pendingDownload={pendingDownload}
                            pendingReceipt={pendingReceipt === order.id}
                            formatPrice={formatPrice}
                            onDownload={handleDownload}
                            onReceipt={() => void handleReceipt(order)}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                </TabsContent>

                <TabsContent value="favorites" className="mt-0">
                  <section aria-labelledby="customer-favorites-title">
                    <div className="border-b border-black pb-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black/55">
                        Guardados
                      </p>
                      <h2
                        id="customer-favorites-title"
                        className="mt-1 text-2xl font-semibold tracking-[-0.04em]"
                      >
                        Mis favoritos
                      </h2>
                      <p className="mt-2 text-sm leading-6 text-black/60">
                        Recursos que marcaste para revisarlos o comprarlos después.
                      </p>
                    </div>

                    {favorites.length > 0 ? (
                      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {favorites.map((favorite) => (
                          <Link
                            key={favorite.id}
                            to="/productos/$productId"
                            params={{ productId: favorite.slug }}
                            className="group flex items-center gap-4 rounded-xl border border-black/15 p-4 transition hover:border-black"
                          >
                            <span className="flex h-24 w-18 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-black text-white">
                              {favorite.coverImage ? (
                                <img
                                  src={favorite.coverImage}
                                  alt={`Portada de ${favorite.title}`}
                                  className="h-full w-full object-contain"
                                  loading="lazy"
                                />
                              ) : (
                                <BookOpen className="h-5 w-5" />
                              )}
                            </span>
                            <span className="min-w-0">
                              <span className="line-clamp-2 text-sm font-semibold">
                                {favorite.title}
                              </span>
                              <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-black/55 group-hover:text-black">
                                Ver recurso
                                <ArrowRight className="h-3.5 w-3.5" />
                              </span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-6 rounded-xl border border-black/15 px-6 py-14 text-center">
                        <Heart className="mx-auto h-7 w-7" />
                        <h3 className="mt-4 text-lg font-semibold">Aún no guardaste favoritos</h3>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-black/60">
                          Usa el corazón de una ficha para guardar un recurso en esta pestaña.
                        </p>
                        <Button asChild className="mt-6">
                          <Link to="/productos/catalogo">
                            Explorar recursos
                            <ArrowRight />
                          </Link>
                        </Button>
                      </div>
                    )}
                  </section>
                </TabsContent>

                <TabsContent value="contributions" className="mt-0">
                  <AccountEpisodeContributions user={user} />
                </TabsContent>

                <TabsContent value="coupons" className="mt-0">
                  <section aria-labelledby="customer-coupons-title">
                    <div className="border-b border-black pb-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black/55">
                        Beneficios
                      </p>
                      <h2 id="customer-coupons-title" className="mt-1 text-2xl font-semibold tracking-[-0.04em]">
                        Mi cuponera
                      </h2>
                      <p className="mt-2 text-sm leading-6 text-black/60">
                        Consulta el estado de tus descuentos y copia los que aún estén disponibles.
                      </p>
                    </div>

                    {coupons.length > 0 ? (
                      <div className="mt-6 grid gap-4 sm:grid-cols-2">
                        {coupons.map((coupon) => (
                          <article key={coupon.code} className="overflow-hidden rounded-xl border border-black/15">
                            <div className="flex items-center justify-between bg-black p-5 text-white">
                              <div>
                                <p className="text-xs uppercase tracking-[0.16em] text-white/55">Descuento</p>
                                <p className="mt-1 text-3xl font-semibold">{coupon.percent}%</p>
                              </div>
                              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${coupon.status === "available" ? "bg-white text-black" : "bg-white/15 text-white/60"}`}>
                                {coupon.status === "available" ? "Disponible" : coupon.status === "used" ? "Usado" : "Vencido"}
                              </span>
                            </div>
                            <div className="p-5">
                              <p className="text-xs uppercase tracking-[0.14em] text-black/45">Código</p>
                              <p className="mt-2 font-mono text-lg font-semibold tracking-wider">{coupon.code}</p>
                              {coupon.status === "available" ? (
                                <>
                                  <p className="mt-3 text-xs text-black/55">
                                    Vence {coupon.expiresAt ? formatDate(coupon.expiresAt) : "7 días después de su creación"}.
                                  </p>
                                  <Button className="mt-5 w-full" onClick={() => void copyCoupon(coupon.code)}>
                                    <Copy />
                                    Copiar cupón
                                  </Button>
                                </>
                              ) : (
                                <p className="mt-5 text-sm text-black/55">
                                  {coupon.status === "used"
                                    ? `Utilizado${coupon.usedAt ? ` el ${formatDate(coupon.usedAt)}` : " en una compra"}.`
                                    : `Venció${coupon.expiresAt ? ` el ${formatDate(coupon.expiresAt)}` : " después de 7 días"}.`}
                                </p>
                              )}
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-6 rounded-xl border border-black/15 px-6 py-12 text-center">
                        <BadgePercent className="mx-auto h-7 w-7" />
                        <h3 className="mt-4 text-lg font-semibold">Obtén tu cupón de bienvenida</h3>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-black/60">
                          Si ya confirmaste tu suscripción al pódcast, genera aquí tu descuento único del 15%.
                        </p>
                        <Button className="mt-6" disabled={couponLoading} onClick={() => void claimNewsletterCoupon()}>
                          {couponLoading ? <LoaderCircle className="animate-spin" /> : <BadgePercent />}
                          Verificar y obtener cupón
                        </Button>
                      </div>
                    )}
                  </section>
                </TabsContent>

                <TabsContent value="profile" className="mt-0">
                  <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                    <section className="rounded-xl border border-black/15 p-5 sm:p-6">
                      <div className="flex items-center gap-4 border-b border-black/10 pb-5">
                        <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-black text-white">
                          {user.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt=""
                              referrerPolicy="no-referrer"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <UserRound className="h-7 w-7" />
                          )}
                        </span>
                        <div className="min-w-0">
                          <h2 className="truncate text-lg font-semibold">{customerName}</h2>
                          <p className="mt-0.5 truncate text-sm text-black/55">{customerEmail}</p>
                        </div>
                      </div>

                      <dl className="mt-6 grid gap-5 text-sm sm:grid-cols-3">
                        <AccountDetail
                          icon={<BadgeCheck />}
                          label="Correo"
                          value={user.emailVerified ? "Verificado" : "Pendiente de verificar"}
                        />
                        <AccountDetail
                          icon={<CalendarDays />}
                          label="Cuenta creada"
                          value={formatDate(user.metadata.creationTime)}
                        />
                        <AccountDetail
                          icon={<Clock3 />}
                          label="Último acceso"
                          value={formatDate(user.metadata.lastSignInTime, true)}
                        />
                      </dl>

                      <div className="mt-6 grid gap-2 border-t border-black/10 pt-6 sm:grid-cols-3">
                        {customerEmail && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={accountAction !== null}
                            onClick={() =>
                              void runAccountAction(
                                "password",
                                () => resetPassword(customerEmail),
                                "Te enviamos un enlace para cambiar tu contraseña.",
                              )
                            }
                          >
                            {accountAction === "password" ? (
                              <LoaderCircle className="animate-spin" />
                            ) : (
                              <ShieldCheck />
                            )}
                            Cambiar contraseña
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={privacyAction !== null}
                          onClick={() => void exportPersonalData()}
                        >
                          {privacyAction === "export" ? (
                            <LoaderCircle className="animate-spin" />
                          ) : (
                            <FileDown />
                          )}
                          Exportar mis datos
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={privacyAction !== null}
                          onClick={() => void requestAccountDeletion()}
                        >
                          {privacyAction === "delete" ? (
                            <LoaderCircle className="animate-spin" />
                          ) : (
                            <UserX />
                          )}
                          Solicitar eliminación
                        </Button>
                      </div>
                      {privacyRequests[0] && (
                        <p className="mt-4 rounded-lg bg-black/[0.04] px-3 py-3 text-sm leading-6 text-black/65">
                          Solicitud {privacyRequests[0].id.slice(0, 8)}:{" "}
                          <strong>{privacyRequests[0].status}</strong>
                        </p>
                      )}
                    </section>

                    <aside className="rounded-xl border border-black bg-black p-5 text-white">
                      <LifeBuoy className="h-5 w-5" />
                      <h2 className="mt-4 text-lg font-semibold">¿Necesitas ayuda?</h2>
                      <p className="mt-2 text-sm leading-6 text-white/65">
                        Incluye el número del pedido para que podamos ayudarte más rápido.
                      </p>
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="mt-5 w-full border-white bg-white text-black hover:bg-black hover:text-white"
                      >
                        <Link to="/productos/tickets">
                          Contactar soporte
                          <ArrowRight />
                        </Link>
                      </Button>
                    </aside>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  compact = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  compact?: boolean;
}) {
  return (
    <div className="rounded-xl border border-black/15 p-5">
      <div className="flex items-center justify-between text-black/40">
        <span className="text-xs font-semibold uppercase tracking-[0.12em]">{label}</span>
        <span className="[&_svg]:h-4 [&_svg]:w-4">{icon}</span>
      </div>
      <p className={`mt-4 font-semibold tracking-[-0.04em] ${compact ? "text-lg" : "text-3xl"}`}>
        {value}
      </p>
    </div>
  );
}

function AccountDetail({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 text-black/35 [&_svg]:h-4 [&_svg]:w-4">{icon}</span>
      <div>
        <dt className="text-black/40">{label}</dt>
        <dd className="mt-0.5 font-medium text-black">{value}</dd>
      </div>
    </div>
  );
}

function OrderCard({
  order,
  user,
  pendingDownload,
  pendingReceipt,
  formatPrice,
  onDownload,
  onReceipt,
}: {
  order: CustomerOrder;
  user: User;
  pendingDownload: string;
  pendingReceipt: boolean;
  formatPrice: (amount: number, currency?: CurrencyCode) => string;
  onDownload: (orderId: string, item: CustomerOrderItem) => void;
  onReceipt: () => void;
}) {
  const { getProduct } = useCatalog();
  const status = STATUS_DETAILS[order.status];
  const StatusIcon = status.Icon;
  const [reviewingProductId, setReviewingProductId] = useState("");

  return (
    <article className="overflow-hidden rounded-xl border border-black/15">
      <header className="flex flex-col justify-between gap-4 border-b border-black/10 p-5 sm:flex-row sm:items-start">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${status.className}`}
            >
              <StatusIcon className="h-3 w-3" />
              {status.label}
            </span>
            <span className="text-xs text-black/60">{formatDate(order.createdAt, true)}</span>
          </div>
          <p className="mt-3 text-xs text-black/45">
            Número de Pedido: <span className="font-mono text-black">#{order.id}</span>
          </p>
          {(order.provider || order.transactionId) && (
            <p className="mt-1 text-xs text-black/60">
              {[order.provider, order.transactionId].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
        <div className="sm:text-right">
          <p className="text-xs uppercase tracking-[0.12em] text-black/60">Total</p>
          <p className="mt-1 text-lg font-semibold">{formatPrice(order.total, order.currency)}</p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 -mx-3 sm:ml-auto border border-black"
            disabled={pendingReceipt}
            onClick={onReceipt}
          >
            {pendingReceipt ? <LoaderCircle className="animate-spin" /> : <FileText />}
            {order.receiptUrl ? "Recibo del pago" : "Ver recibo"}
            {order.receiptUrl && <ExternalLink />}
          </Button>
        </div>
      </header>

      <div className="divide-y divide-black/10">
        {order.items.length === 0 ? (
          <p className="p-5 text-xs text-black/45">
            El detalle de los recursos aún no está disponible.
          </p>
        ) : (
          order.items.map((item) => {
            const key = `${order.id}:${item.productId}`;
            const catalogProduct = getProduct(item.productId);
            const coverImage =
              catalogProduct?.coverThumbnail ?? catalogProduct?.coverImage ?? item.coverImage;
            return (
              <div key={key}>
                <div className="flex items-center gap-4 p-5">
                  <div className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[5px] bg-black/5">
                    {coverImage ? (
                      <img
                        src={coverImage}
                        alt=""
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <BookOpen className="h-5 w-5 text-black/35" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold leading-snug">{item.title}</h3>
                    <p className="mt-1 text-xs text-black/60">
                      {item.quantity > 1 && `${item.quantity} unidades · `}
                      {formatPrice(item.unitPrice * item.quantity, item.currency)}
                    </p>
                    {item.canDownload && (
                      <p className="mt-1 text-xs font-medium text-black/65">
                        Disponible desde tu cuenta
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                    {order.status === "paid" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setReviewingProductId((current) =>
                            current === item.productId ? "" : item.productId,
                          )
                        }
                      >
                        <Star className="fill-amber-400 text-amber-400" />
                        <span className="hidden sm:inline">Calificar</span>
                      </Button>
                    )}
                    {item.canDownload ? (
                      <Button
                        size="sm"
                        disabled={Boolean(pendingDownload)}
                        onClick={() => onDownload(order.id, item)}
                      >
                        {pendingDownload === key ? (
                          <LoaderCircle className="animate-spin" />
                        ) : (
                          <Download />
                        )}
                        <span className="hidden sm:inline">Descargar</span>
                      </Button>
                    ) : order.status === "paid" ? (
                      <Button asChild variant="outline" size="sm">
                        <Link to="/productos/tickets">
                          <LifeBuoy />
                          <span className="hidden sm:inline">Solicitar acceso</span>
                        </Link>
                      </Button>
                    ) : (
                      <span className="text-xs text-black/60">Disponible al aprobarse</span>
                    )}
                  </div>
                </div>
                {reviewingProductId === item.productId && (
                  <AccountReviewForm
                    user={user}
                    productId={item.productId}
                    productTitle={item.title}
                    onClose={() => setReviewingProductId("")}
                  />
                )}
              </div>
            );
          })
        )}
      </div>
    </article>
  );
}

function AccountReviewForm({
  user,
  productId,
  productTitle,
  onClose,
}: {
  user: User;
  productId: string;
  productTitle: string;
  onClose: () => void;
}) {
  const [rating, setRating] = useState(0);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasReview, setHasReview] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void user
      .getIdToken()
      .then((idToken) =>
        appCheckFetch(`/api/product-engagement?${new URLSearchParams({ productId })}`, {
          headers: { Authorization: `Bearer ${idToken}` },
          signal: controller.signal,
        }),
      )
      .then(async (response) => {
        const data = (await response.json().catch(() => null)) as {
          viewer?: {
            myReview?: { rating: number; description: string };
          };
        } | null;
        if (!response.ok) return;
        const review = data?.viewer?.myReview;
        if (review) {
          setRating(review.rating);
          setDescription(review.description);
          setHasReview(true);
        }
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          toast.error("No pudimos consultar tu reseña anterior.");
        }
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [productId, user]);

  async function submitReview(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (rating < 1) {
      toast.error("Selecciona entre 1 y 5 estrellas.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await appCheckFetch("/api/product-engagement", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await user.getIdToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "review",
          productId,
          rating,
          description: description.trim(),
        }),
      });
      const data = (await response.json().catch(() => null)) as {
        review?: unknown;
        message?: string;
      } | null;
      if (!response.ok || !data?.review) {
        throw new Error(data?.message ?? "No pudimos publicar la reseña.");
      }
      void trackEvent("review_submit", {
        item_id: productId,
        item_name: productTitle,
        rating,
      });
      setHasReview(true);
      toast.success("Tu reseña verificada ya está publicada.");
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos publicar la reseña.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={submitReview}
      className="border-t border-black/10 bg-amber-50/60 p-5 sm:pl-[6.75rem]"
    >
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-black/60">
            Compra verificada
          </p>
          <h4 className="mt-1 text-sm font-semibold">
            {hasReview ? "Edita tu reseña" : `¿Qué te pareció ${productTitle}?`}
          </h4>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer text-left text-xs font-semibold underline sm:text-right"
        >
          Cerrar
        </button>
      </div>
      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-xs text-black/50">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Preparando formulario…
        </div>
      ) : (
        <>
          <div className="mt-4 flex gap-1" aria-label="Calificación">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className="cursor-pointer rounded p-1 focus:outline-none focus:ring-2 focus:ring-amber-400"
                aria-label={`${star} estrella${star === 1 ? "" : "s"}`}
                aria-pressed={rating === star}
              >
                <Star
                  className={`h-7 w-7 ${
                    star <= rating
                      ? "fill-amber-400 text-amber-400"
                      : "fill-transparent text-black/20"
                  }`}
                />
              </button>
            ))}
          </div>
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            minLength={10}
            maxLength={500}
            required
            className="mt-3 min-h-24 bg-white"
            placeholder="Escribe una descripción breve de tu experiencia con el libro."
          />
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-xs text-black/60">{description.length}/500</span>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting && <LoaderCircle className="animate-spin" />}
              {hasReview ? "Actualizar reseña" : "Publicar reseña"}
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
